import { axiosInstance } from './api';
import type { Paginated } from './types';

// ── DTOs ─────────────────────────────────────────────────────────────────────

export interface CheckoutZoneDto {
  id: string;
  group: string;
  name: string;
  type: 'QUEUE' | 'CASHIER' | 'STAFF' | 'ORDER_WAITING';
  operations_dashboard_id?: string | null;
}

export interface CheckoutQueueDto {
  id: string;
  checkout_zone: string;
  max_allowed_size: number;
}

export interface StaffZoneDto {
  id: string;
  checkout_zone: string;
  max_workers: number;
}

export interface PolygonGroupDto {
  id: string;
  name?: string | null;
  areas: string[];
}

// ── Create/Patch types ───────────────────────────────────────────────────────

type CheckoutZoneCreate = Omit<CheckoutZoneDto, 'id'>;
type CheckoutZonePatch = Partial<Omit<CheckoutZoneDto, 'id'>>;

type CheckoutQueueCreate = Omit<CheckoutQueueDto, 'id'>;
type CheckoutQueuePatch = Partial<Omit<CheckoutQueueDto, 'id'>>;

type StaffZoneCreate = Omit<StaffZoneDto, 'id'>;
type StaffZonePatch = Partial<Omit<StaffZoneDto, 'id'>>;

type PolygonGroupCreate = { name?: string | null; areas?: string[] };
type PolygonGroupPatch = Partial<Omit<PolygonGroupDto, 'id'>>;

// ── Checkout Zones ───────────────────────────────────────────────────────────

export const listCheckoutZones = (pageSize = 1000) =>
  axiosInstance.get<Paginated<CheckoutZoneDto>>(`/checkout_control/checkout_zone/?page_size=${pageSize}`);

export const listCheckoutZonesByZone = (zoneId: string, pageSize = 1000) =>
  axiosInstance.get<Paginated<CheckoutZoneDto>>(
    `/checkout_control/checkout_zone/?group__areas__camera__zone=${zoneId}&page_size=${pageSize}`,
  );

export const createCheckoutZone = (payload: CheckoutZoneCreate) =>
  axiosInstance.post<CheckoutZoneDto>('/checkout_control/checkout_zone/', payload);

export const patchCheckoutZone = (id: string, payload: CheckoutZonePatch) =>
  axiosInstance.patch<CheckoutZoneDto>(`/checkout_control/checkout_zone/${id}/`, payload);

export const deleteCheckoutZone = (id: string) =>
  axiosInstance.delete(`/checkout_control/checkout_zone/${id}/`);

export const respawnCheckoutZoneGrafana = (id: string) =>
  axiosInstance.post<{ status: string }>(
    `/checkout_control/checkout_zone/${id}/respawn-grafana/`,
  );

// ── Checkout Queues ──────────────────────────────────────────────────────────

export const listCheckoutQueues = (pageSize = 1000) =>
  axiosInstance.get<Paginated<CheckoutQueueDto>>(`/checkout_control/checkout_queue/?page_size=${pageSize}`);

export const createCheckoutQueue = (payload: CheckoutQueueCreate) =>
  axiosInstance.post<CheckoutQueueDto>('/checkout_control/checkout_queue/', payload);

export const patchCheckoutQueue = (id: string, payload: CheckoutQueuePatch) =>
  axiosInstance.patch<CheckoutQueueDto>(`/checkout_control/checkout_queue/${id}/`, payload);

// ── Staff Zones ──────────────────────────────────────────────────────────────

export const listStaffZones = (pageSize = 1000) =>
  axiosInstance.get<Paginated<StaffZoneDto>>(`/checkout_control/staff_zone/?page_size=${pageSize}`);

export const createStaffZone = (payload: StaffZoneCreate) =>
  axiosInstance.post<StaffZoneDto>('/checkout_control/staff_zone/', payload);

export const patchStaffZone = (id: string, payload: StaffZonePatch) =>
  axiosInstance.patch<StaffZoneDto>(`/checkout_control/staff_zone/${id}/`, payload);

// ── Polygon Groups ───────────────────────────────────────────────────────────

export const listPolygonGroups = (pageSize = 1000) =>
  axiosInstance.get<Paginated<PolygonGroupDto>>(`/core/api/polygon-groups/?page_size=${pageSize}`);

export const createPolygonGroup = (payload: PolygonGroupCreate) =>
  axiosInstance.post<PolygonGroupDto>('/core/api/polygon-groups/', payload);

export const patchPolygonGroup = (id: string, payload: PolygonGroupPatch) =>
  axiosInstance.patch<PolygonGroupDto>(`/core/api/polygon-groups/${id}/`, payload);

export const deletePolygonGroup = (id: string) =>
  axiosInstance.delete(`/core/api/polygon-groups/${id}/`);
